import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import axios from "axios";
import toast from "react-hot-toast";

const GamesChart = () => {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [dates, setDates] = useState([]);
    const [counts, setCounts] = useState([]);
    const [games, setGames] = useState([]);

    const token = sessionStorage.getItem("bearer_token");
    const auth_token = sessionStorage.getItem("token");

    // Set default date range to last 7 days
    useEffect(() => {
        const today = new Date();
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(today.getDate() - 6);

        const formattedStart = sevenDaysAgo.toISOString().split("T")[0];
        const formattedEnd = today.toISOString().split("T")[0];

        setStartDate(formattedStart);
        setEndDate(formattedEnd);
    }, []);

    // Fetch chart data on date change
    useEffect(() => {
        if (startDate && endDate) {
            fetchChartData();
            fetchDetailsData();
        }
    }, [startDate, endDate]);

    const fetchChartData = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}api/sales/get-game-chart`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    auth_token: `${auth_token}`,
                },
                params: { startDate, endDate },
            });

            const responseData = res.data.data;
            const dateArray = responseData.map((item) => item.date);
            const countArray = responseData.map((item) => item.count);

            setDates(dateArray);
            setCounts(countArray);
        } catch (error) {
            console.error("Error fetching chart data:", error);
            setDates([]);
            setCounts([]);
        }
    };

    const fetchDetailsData = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}api/sales/get-game-details`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    auth_token: `${auth_token}`,
                },
                params: { startDate, endDate },
            });

            setGames(res.data.data || []);
        } catch (error) {
            console.error("Error fetching game details:", error);
        }
    };

    // Render chart when data updates
    useEffect(() => {
        if (dates.length && counts.length) {
            const options = {
                series: [
                    {
                        name: "Games Played",
                        data: counts,
                    },
                ],
                chart: {
                    type: "area",
                    height: 400,
                    animations: { enabled: true },
                },
                xaxis: {
                    categories: dates,
                },
                yaxis: {
                    min: 0,
                    labels: {
                        formatter: (val) => Math.floor(val),
                    },
                },
                stroke: {
                    curve: "smooth",
                },
                fill: {
                    type: "gradient",
                    gradient: {
                        shade: "light",
                        opacityFrom: 0.4,
                        opacityTo: 0,
                    },
                },
            };

            const chart = new ApexCharts(document.querySelector("#game-chart"), options);
            chart.render();

            return () => chart.destroy();
        }
    }, [dates, counts]);

    // format date for table
    const formatDate = (dateString) => {
        const date = new Date(dateString.replace(" ", "T")); // Replace space with T for compatibility
        const options = { month: 'long', day: 'numeric', year: 'numeric' };
        const formattedDate = date.toLocaleDateString('en-US', options);
        const time = date.toLocaleTimeString('en-US', { hour12: false });
        return `${formattedDate} ${time}`;
    };

    const exportToCSV = () => {
        if (games.length === 0) {
            toast.error("No data to export.");
            return;
        }

        const headers = [
            "User Name",
            "Game Type",
            "City",
            "Game Level",
            "Credits",
            "Is Winner",
            "Total Matching Number",
            "Prize Money",
            "Played On"
        ];

        const rows = games.map((game) => [
            game.User?.name || "N/A",
            game.GameType?.name || "N/A",
            game.City?.name
                ? `${game.City.name}, ${game.City.State?.name || ""}, ${game.City.State?.Country?.name || ""}`
                : "N/A",
            game.gameLevel || "N/A",
            game.credits || "0",
            game.isWinner ? "Yes" : "No",
            game.matchingNumbers ?? "0",
            game.prizeMoney ?? "0",
            formatDate(game.createdAt),
        ]);

        const csvContent = [
            headers.join(","),
            ...rows.map(row =>
                row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(",")
            )
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `game_details_${startDate}_to_${endDate}.csv`;
        link.click();

        URL.revokeObjectURL(url);
    };


    return (
        <div className="p-4">
            <div className="flex flex-wrap items-center gap-4 mb-4">
                <div>
                    <label className="text-sm">Start Date:</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="ml-2 border rounded px-2 py-1"
                    />
                </div>
                <div>
                    <label className="text-sm">End Date:</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="ml-2 border rounded px-2 py-1"
                    />
                </div>
            </div>

            <div id="game-chart"></div>

            <div className="w-full  p-4 bg-white border border-gray-200 rounded-lg shadow sm:p-8 dark:bg-gray-800 dark:border-gray-700">

                <div className="flex justify-between">
                    <div className="flex flex-col text-left">
                        <h1 className="text-xl text-black font-bold">User Details</h1>
                        <h1 className="text-md text-gray-400">Games Played by Players</h1>
                    </div>

                    <div>
                        <button
                            onClick={exportToCSV}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow"
                        >
                            Download CSV
                        </button>
                    </div>


                </div>

                <div className="relative overflow-x-auto custom-scrollbar sm:rounded-lg mt-5">

                    <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">User Name</th>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">Game Type</th>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">City</th>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">Game Level</th>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">Credits</th>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">Is Winner</th>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">Total Matching Number</th>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">Prize Money</th>
                                <th scope="col" className="px-6 py-3 whitespace-normal break-words">Played On</th>
                            </tr>
                        </thead>
                        <tbody>
                            {games.length > 0 ? (
                                games.map((game) => (
                                    <tr key={game.id} className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700">
                                        <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-normal break-words dark:text-white">{game.User ? game.User.name : "N/A"}</th>
                                        <td className="px-6 py-4 whitespace-normal break-words">{game.GameType ? game.GameType.name : "N/A"}</td>
                                        <td className="px-6 py-4 whitespace-normal break-words">{game?.City?.name ? game?.City?.name + ', ' + game?.City?.State?.name + ', ' + game?.City?.State?.Country?.name : 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-normal break-words">{game.gameLevel ? game.gameLevel : "N/A"}</td>
                                        <td className="px-6 py-4 whitespace-normal break-words">{game.credits ? game.credits : "0"}</td>
                                        <td className="px-6 py-4 whitespace-normal break-words">{game.isWinner ? 'Yes' : 'No'}</td>
                                        <td className="px-6 py-4 whitespace-normal break-words">{game.matchingNumbers}</td>
                                        <td className="px-6 py-4 whitespace-normal break-words">{game.prizeMoney}</td>
                                        <td className="px-6 py-4 whitespace-normal break-words">{formatDate(game.createdAt)}</td>

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-4 whitespace-normal break-words">No Games Found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default GamesChart;
